<script lang="ts" setup>
import { ULink } from '#components'

defineProps({
  callsPerMonth: {
    default: 0,
    type: Number,
  },
  color: {
    default: 'indigo',
    type: String as PropType<ApiTocCategoryEndpointColor>,
  },
  costPerCall: {
    default: 0,
    type: Number,
  },
  costPerCompany: {
    default: 0,
    type: Number,
  },
  id: {
    default: '',
    type: String,
  },
  isNew: {
    default: false,
    type: Boolean,
  },
  method: {
    default: 'GET',
    type: String as PropType<ApiEndpointMethodMethod>,
  },
  path: {
    default: '',
    type: String,
  },
  text: {
    default: '',
    type: String,
  },
  to: {
    default: undefined,
    type: [Object, String] as PropType<RawLocation | string>,
  },
})

const { t } = useI18n()
</script>

<template>
  <Component
    :is="to ? ULink : 'div'"
    class="gradient-texture transition-base shadow-texture ring-texture ring-texture-active flex w-full cursor-pointer flex-col gap-2 overflow-hidden rounded-lg p-4 text-left text-base outline-none"
    :to="to"
  >
    <div class="flex w-full items-start gap-2">
      <ApiEndpointMethod
        class="mt-0.5"
        :method="method"
      />

      <div class="flex flex-1 items-center gap-2">
        <p>{{ text }}</p>

        <UBadge
          v-if="isNew"
          :color="color"
          size="xs"
          variant="soft"
        >
          {{ t('globals.new') }}
        </UBadge>
      </div>

      <UBadge
        class="hidden md:inline-block"
        :color="color"
        :label="path"
        variant="soft"
      />
    </div>

    <div
      v-if="callsPerMonth || costPerCall || costPerCompany"
      class="flex items-center justify-between gap-2 border-t border-gray-200 pb-4 pt-2 dark:border-gray-700"
    >
      <div class="flex items-center gap-2">
        <span
          v-if="costPerCompany"
          class="text-sm text-gray-500 dark:text-gray-400"
        >
          <BaseCoin
            :mono="false"
            size="sm"
          >
            <span class="-mb-6 flex flex-col">
              <span>
                {{ t('costPerCompany', { cost: costPerCompany }) }}
              </span>

              <span class="text-sm text-gray-400 dark:text-gray-500">
                {{ creditsToCents(costPerCompany) }}$
              </span>
            </span>
          </BaseCoin>
        </span>

        <span
          v-if="costPerCall"
          class="text-sm text-gray-500 dark:text-gray-400"
        >
          <BaseCoin
            :mono="false"
            size="sm"
          >
            <span class="-mb-6 flex flex-col">
              <span>
                {{ t('costPerCall', { cost: costPerCall }) }}
              </span>

              <span class="text-sm text-gray-400 dark:text-gray-500">
                {{ creditsToCents(costPerCall) }}$
              </span>
            </span>
          </BaseCoin>
        </span>
      </div>

      <span
        v-if="callsPerMonth"
        class="text-sm text-gray-500 dark:text-gray-400"
      >
        {{ t('callsPerMonth', { count: numbro(callsPerMonth, { average: true }) }) }}
      </span>
    </div>
  </Component>
</template>

<i18n lang="json">
{
  "en": {
    "callsPerMonth": "{count}+ calls per month",
    "costPerCall": "{cost} credit per call | {cost} credits per call",
    "costPerCompany": "{cost} credit per company | {cost} credits per company"
  },
  "es": {
    "callsPerMonth": "{count}+ llamadas por mes",
    "costPerCall": "{cost} crédito por llamada | {cost} créditos por llamada",
    "costPerCompany": "{cost} crédito por empresa | {cost} créditos por empresa"
  },
  "fr": {
    "callsPerMonth": "{count}+ appels par mois",
    "costPerCall": "{cost} crédit par appel | {cost} créditos par appel",
    "costPerCompany": "{cost} crédit par entreprise | {cost} créditos par entreprise"
  }
}
</i18n>
